<template>
  <div class="applicant-detail-container page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <router-link 
            to="/applicants" 
            class="btn btn-outline-secondary btn-icon me-3"
          >
            <i class="fas fa-arrow-left"></i>
          </router-link>
          Bewerber Details
        </h1>
      </div>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Wird geladen...</span>
        </div>
      </div>

      <template v-else-if="applicant">
        <div class="row">
          <!-- Bewerber Informationen -->
          <div class="col-lg-4 mb-4">
            <Card animateIn="fade-in-up">
              <div class="text-center mb-4">
                <div class="avatar avatar-lg mx-auto mb-3">
                  {{ getInitials(applicant.full_name) }}
                </div>
                <h3 class="mb-1">{{ applicant.full_name }}</h3>
                <p class="text-muted mb-0">
                  <span class="me-3">
                    <i class="fas fa-envelope me-1"></i>
                    {{ applicant.email }}
                  </span>
                  <span v-if="applicant.phone">
                    <i class="fas fa-phone me-1"></i>
                    {{ applicant.phone }}
                  </span>
                </p>
              </div>

              <hr>

              <div class="applicant-stats">
                <div class="stat-item">
                  <span class="stat-label">Geschlecht</span>
                  <span class="stat-value">{{ getGenderText(applicant.gender) }}</span>
                </div>

                <div class="stat-item">
                  <span class="stat-label">Status</span>
                  <span 
                    class="badge"
                    :class="getStatusClass(applicant.status)"
                  >
                    {{ getStatusText(applicant.status) }}
                  </span>
                </div>

                <div class="stat-item">
                  <span class="stat-label">Tests abgeschlossen</span>
                  <span class="stat-value">
                    {{ getCompletedTestsCount(applicant) }}/{{ getTotalTestsCount(applicant) }}
                  </span>
                </div>

                <div class="stat-item">
                  <span class="stat-label">Durchschnittliche Punktzahl</span>
                  <div class="d-flex align-items-center">
                    <div class="progress flex-grow-1 me-2">
                      <div 
                        class="progress-bar" 
                        :class="getScoreClass(getAverageScore(applicant))"
                        :style="{ width: getAverageScore(applicant) + '%' }"
                      ></div>
                    </div>
                    <span class="stat-value">{{ getAverageScore(applicant) }}%</span>
                  </div>
                </div>

                <div class="stat-item">
                  <span class="stat-label">Bewerber seit</span>
                  <span class="stat-value">{{ formatDate(applicant.created_at) }}</span>
                </div>
              </div>
            </Card>
          </div>

          <!-- Test Ergebnisse -->
          <div class="col-lg-8">
            <Card animateIn="fade-in-up" class="mb-4">
              <template v-slot:header>
                <div class="d-flex justify-content-between align-items-center">
                  <h4 class="card-title mb-0">Test Ergebnisse</h4>
                </div>
              </template>

              <div v-if="applicant.entries && applicant.entries.length > 0">
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Test</th>
                        <th>Punktzahl</th>
                        <th>Abgeschlossen am</th>
                        <th class="text-end">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="entry in sortedEntries" :key="entry.id">
                        <td>{{ entry.test.name }}</td>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="progress flex-grow-1 me-2" style="width: 100px;">
                              <div 
                                class="progress-bar" 
                                :class="getScoreClass(entry.score_percentage)"
                                :style="{ width: entry.score_percentage + '%' }"
                              ></div>
                            </div>
                            <span>{{ entry.score_percentage }}%</span>
                          </div>
                        </td>
                        <td>{{ formatDate(entry.created_at) }}</td>
                        <td class="text-end">
                          <button 
                            class="btn-icon btn-icon-primary with-tooltip"
                            @click="viewTestResults(entry)"
                            data-tooltip="Ergebnisse anzeigen"
                          >
                            <i class="fas fa-eye"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center py-4">
                <p class="text-muted mb-0">Noch keine Tests abgeschlossen.</p>
              </div>
            </Card>
          </div>
        </div>
      </template>

      <div v-else class="text-center py-5">
        <p class="text-muted mb-0">Bewerber nicht gefunden.</p>
      </div>
    </div>
  </div>
</template>

<script>
import Card from '@/components/ui/Card.vue';
import { formatDate } from '@/utils/date';
import { useApplicantsStore } from '@/stores/applicants';
import { useTestsStore } from '@/stores/tests';

export default {
  name: 'ApplicantDetail',
  
  components: {
    Card
  },
  
  setup() {
    const applicantsStore = useApplicantsStore();
    const testsStore = useTestsStore();
    
    return {
      applicantsStore,
      testsStore
    };
  },

  data() {
    return {
      loading: false,
      selectedTestId: '',
      testToUnassign: null
    };
  },

  computed: {
    applicant() {
      return this.applicantsStore.getCurrentApplicant;
    },
    
    allTests() {
      return this.testsStore.getAllTests;
    },

    applicantId() {
      return this.$route.params.id;
    },

    sortedEntries() {
      if (!this.applicant?.entries) return [];
      return [...this.applicant.entries].sort((a, b) => {
        return new Date(b.completed_at) - new Date(a.completed_at);
      });
    },

    availableTests() {
      if (!this.applicant || !this.allTests) return [];
      
      const assignedTestIds = this.applicant.assigned_tests?.map(t => t.id) || [];
      return this.allTests.filter(test => 
        !assignedTestIds.includes(test.id) && test.active
      );
    },

    canAssignTests() {
      return this.availableTests.length > 0;
    }
  },

  methods: {
    async fetchApplicant(id) {
      return this.applicantsStore.fetchApplicant(id);
    },
    
    async fetchTests() {
      return this.testsStore.fetchTests();
    },
    
    async assignTestAction(payload) {
      return this.applicantsStore.assignTest(payload);
    },
    
    async unassignTestAction(payload) {
      return this.applicantsStore.unassignTest(payload);
    },

    getInitials(name) {
      if (!name) return '';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase();
    },

    getGenderText(gender) {
      const texts = {
        male: 'Männlich',
        female: 'Weiblich',
        diverse: 'Divers'
      };
      return texts[gender] || 'Nicht angegeben';
    },

    getCompletedTestsCount(applicant) {
      return applicant.entries?.length || 0;
    },

    getTotalTestsCount(applicant) {
      return applicant.assigned_tests?.length || 0;
    },

    getAverageScore(applicant) {
      if (!applicant.entries || applicant.entries.length === 0) return 0;
      
      const totalScore = applicant.entries.reduce((sum, entry) => {
        return sum + (entry.score_percentage || 0);
      }, 0);
      
      return Math.round(totalScore / applicant.entries.length);
    },

    getScoreClass(score) {
      if (score >= 80) return 'bg-success';
      if (score >= 60) return 'bg-warning';
      return 'bg-danger';
    },

    getStatusClass(status) {
      const classes = {
        pending: 'bg-warning',
        in_progress: 'bg-info',
        completed: 'bg-success',
        failed: 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    getStatusText(status) {
      const texts = {
        pending: 'Ausstehend',
        in_progress: 'In Bearbeitung',
        completed: 'Abgeschlossen',
        failed: 'Nicht bestanden',
        new: 'Neu'
      };
      return texts[status] || status;
    },

    getEntryStatusClass(status) {
      return this.getStatusClass(status);
    },

    getEntryStatusText(status) {
      return this.getStatusText(status);
    },

    getAssignedTestStatusClass(test) {
      const isCompleted = this.isTestCompleted(test);
      return isCompleted ? 'bg-success' : 'bg-warning';
    },

    getAssignedTestStatusText(test) {
      const isCompleted = this.isTestCompleted(test);
      return isCompleted ? 'Abgeschlossen' : 'Ausstehend';
    },

    isTestCompleted(test) {
      return this.applicant.entries?.some(entry => entry.test_id === test.id);
    },

    formatDate(date) {
      return formatDate(date);
    },

    viewTestResults(entry) {
      this.$router.push(`/entries/${entry.id}`);
    },

    showAssignTestModal() {
      this.selectedTestId = '';
      this.$refs.assignTestModal.show();
    },

    async assignTest(bvModalEvent) {
      bvModalEvent.preventDefault();
      
      if (!this.selectedTestId) {
        this.$toast.error('Bitte wählen Sie einen Test aus.');
        return;
      }

      try {
        await this.assignTestAction({
          applicantId: this.applicantId,
          testId: this.selectedTestId
        });
        
        this.$refs.assignTestModal.hide();
        this.$toast.success('Test wurde erfolgreich zugewiesen.');
        
        // Aktualisiere die Bewerber-Daten
        await this.fetchApplicant(this.applicantId);
      } catch (error) {
        this.$toast.error('Fehler beim Zuweisen des Tests.');
        console.error(error);
      }
    },

    confirmUnassignTest(test) {
      this.testToUnassign = test;
      this.$refs.unassignTestModal.show();
    },

    async unassignTest() {
      if (!this.testToUnassign) return;

      try {
        await this.unassignTestAction({
          applicantId: this.applicantId,
          testId: this.testToUnassign.id
        });
        
        this.$toast.success('Test-Zuweisung wurde aufgehoben.');
        this.testToUnassign = null;
        
        // Aktualisiere die Bewerber-Daten
        await this.fetchApplicant(this.applicantId);
      } catch (error) {
        this.$toast.error('Fehler beim Aufheben der Test-Zuweisung.');
        console.error(error);
      }
    }
  },

  async created() {
    this.loading = true;
    try {
      await Promise.all([
        this.fetchApplicant(this.applicantId),
        this.fetchTests()
      ]);
    } catch (error) {
      this.$toast.error('Fehler beim Laden der Daten.');
      console.error(error);
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: var(--bs-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 500;

  &.avatar-lg {
    width: 96px;
    height: 96px;
    font-size: 2rem;
  }
}

.applicant-stats {
  .stat-item {
    margin-bottom: 1.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .stat-label {
      display: block;
      font-size: 0.875rem;
      color: var(--bs-gray-600);
      margin-bottom: 0.5rem;
    }
    
    .stat-value {
      font-weight: 500;
    }
  }
}

.progress {
  height: 6px;
  border-radius: 1rem;
  background-color: #e9ecef;
}
</style> 